import './AgreementsPage.css';

import { FC, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addConsentGiversToMember, ConsentGiver } from 'api/account/addConsentGiversToMember';
import { addFlagToMember, FlagRefKinds } from 'api/account/addFlagToMember';
import { createOrUpdatePatient, updateNpTeenConsent } from 'api/account/createOrUpdatePatient';
import { getReferrer } from 'api/account/getReferrer';
import { signAgreement } from 'api/account/signAgreement';
import { updateConsentToTreat } from 'api/account/updateConsentToTreat';
import { CommonOnboardingPage, MemberProgram } from 'app-pages/pages/constants';
import { BackArrowButton } from 'components/_global/back-button/BackArrowButton';
import { GlobalHeader } from 'components/_global/header/GlobalHeader';
import { Text } from 'components/_temp_ui_common/theme/Text';
import { AccodionContentItem, Accordion } from 'components/accordion/Accordion';
import { Button } from 'components/core/button';
import { FormProvider } from 'components/core/form';
import { ExternalLink } from 'components/core/link/Link';
import { FooterButton } from 'components/structure/footer/FooterButton';
import { FooterPortal } from 'components/structure/footer/FooterPortal';
import { useBrowserContext } from 'contexts/BrowserContext';
import { ConsultProvider, useConsult } from 'contexts/ConsultContext';
import { useCurrentUser } from 'contexts/CurrentUserContext';
import { useEnsureAuthenticated } from 'hooks/useEnsureAuthenticated';
import { useMergeState } from 'hooks/useMergeState';
import { useNpReferral } from 'hooks/useNpReferral';
import { useNpReferralOnly } from 'hooks/useNpReferralOnly';
import { useTrackPageVisit } from 'hooks/useTrackPageVisit';
import { useNavigate, useParams } from 'react-router-dom';

import { useNextStep } from '../../../../api/account/useNextStep';
import { usePatientPendingAgreements } from '../../../../api/account/usePatientPendingAgreements';
import { Agreements, AgreementStatus, isAgreementStatusRequired, isAgreementStatusResolved } from './AgreementsTypes';
import { AgreementIndicator } from './components/AgreementIndicator';
import { AgreementsIntroPanel } from './components/AgreementsIntroPanel';
import { ConsentAgreementBody } from './components/ConsentAgreementBody';

interface AgreementsPageContentProps {
  memberId?: string;
}

export const AgreementsPageContent: FC<AgreementsPageContentProps> = ({ memberId }) => {
  const consultContext = useConsult();
  const nextStep = useNextStep();
  const { data: pendingAgreements, isLoading: isPendingAgreementsLoading } = usePatientPendingAgreements({
    patientId: memberId || consultContext.consult?.patientId,
  });

  const isNpReferralOnly = useNpReferralOnly();
  const isNpReferral = useNpReferral();
  const isNpReferralFlow = isNpReferralOnly || isNpReferral;

  useTrackPageVisit(
    CommonOnboardingPage.Agreements,
    isNpReferralFlow ? MemberProgram.NP : MemberProgram.CORE,
    memberId || consultContext.consult?.patientId,
  );

  const planType = isNpReferralFlow ? 'np' : 'core';
  const push = useNavigate();
  const popNavigation = () => {
    push(-1);
  };

  const [needsConsent, _setNeedsConsent] = useState<boolean>();
  const [agreements, mergeAgreementsState] = useMergeState<Record<Agreements, AgreementStatus>>({
    [Agreements.TermsAndConditions]: AgreementStatus.Initializing,
    [Agreements.MemberFinancialPolicy]: AgreementStatus.Initializing,
    [Agreements.NpInformedConsent]: AgreementStatus.Initializing,
    [Agreements.NpFinancialPolicyPrice]: AgreementStatus.Initializing,
    [Agreements.HealthInfo]: AgreementStatus.Initializing,
    [Agreements.TeenConsent]: AgreementStatus.Initializing,
    [Agreements.NpTeenConsent]: AgreementStatus.Initializing,
    [Agreements.ConsentToCare]: AgreementStatus.Initializing,
  });

  const setNeedsConsent = useCallback(
    (value: boolean) => {
      _setNeedsConsent(value);
      mergeAgreementsState({
        [Agreements.ConsentToCare]: AgreementStatus.Agreed,
      });
    },
    [mergeAgreementsState],
  );
  const [accordionActiveIndex, setAccordionActiveIndex] = useState<number>(0);
  const [state, mergeState] = useMergeState({
    initialized: false,
    isReferral: null as boolean | null,
    referralName: null as string | null,
  });

  const advanceActiveIndex = useCallback(() => {
    setAccordionActiveIndex((a) => a + 1);
  }, [setAccordionActiveIndex]);

  useEffect(() => {
    if (!consultContext.consult) return;
    getReferrer(consultContext.consult.patientId).then((name: string) => {
      if (name) {
        mergeState({ isReferral: true, referralName: name });
      } else if (consultContext.consult?.patient.titleizedMrnSystem) {
        mergeState({
          isReferral: true,
          referralName: consultContext.consult?.patient.titleizedMrnSystem,
        });
      } else {
        mergeState({ isReferral: false });
      }
    });
  }, [consultContext.consult, mergeState]);

  useEffect(() => {
    if (isPendingAgreementsLoading) return;

    const nextState: Record<Agreements, AgreementStatus> = Object.values(Agreements).reduce(
      (acc, agreementType) => {
        acc[agreementType] = pendingAgreements?.includes(agreementType)
          ? AgreementStatus.Waiting
          : AgreementStatus.NotApplicable;

        return acc;
      },
      {} as Record<Agreements, AgreementStatus>,
    );

    mergeAgreementsState(nextState);
    setAccordionActiveIndex(getActiveIndex(nextState));
  }, [pendingAgreements, isPendingAgreementsLoading, mergeAgreementsState, setAccordionActiveIndex, getActiveIndex]);

  const acceptMemberFinancialPolicy = useCallback(async () => {
    if (!consultContext.consult) return;
    mergeAgreementsState({
      [Agreements.MemberFinancialPolicy]: AgreementStatus.Agreed,
    });

    await signAgreement(consultContext.consult.patientId, 'Patient', 'MEMBER_FINANCIAL_POLICY', 'core');

    advanceActiveIndex();
  }, [consultContext.consult, mergeAgreementsState, advanceActiveIndex]);

  const acceptNpInformedConsent = useCallback(async () => {
    if (!consultContext.consult) return;
    mergeAgreementsState({
      [Agreements.NpInformedConsent]: AgreementStatus.Agreed,
    });

    await signAgreement(consultContext.consult.patientId, 'Patient', 'NEURO_PSYCH_EVAL_INFORMED_CONSENT', planType);

    advanceActiveIndex();
  }, [consultContext.consult, mergeAgreementsState, planType, advanceActiveIndex]);

  const acceptNpFinancialPolicyPrice = useCallback(async () => {
    if (!consultContext.consult) return;
    mergeAgreementsState({
      [Agreements.NpFinancialPolicyPrice]: AgreementStatus.Agreed,
    });

    await signAgreement(
      consultContext.consult.patientId,
      'Patient',
      'NEURO_PSYCH_EVAL_FINANCIAL_POLICY_AND_PRICING',
      planType,
    );

    advanceActiveIndex();
  }, [consultContext.consult, mergeAgreementsState, planType, advanceActiveIndex]);

  /* Terms and Conditions handler */
  const acceptToC = useCallback(async () => {
    if (!consultContext.consult) return;
    mergeAgreementsState({
      [Agreements.TermsAndConditions]: AgreementStatus.Agreed,
    });

    await signAgreement(consultContext.consult.patientId, 'Patient', 'TERMS_AND_CONDITIONS', 'core');

    advanceActiveIndex();
  }, [consultContext.consult, mergeAgreementsState, advanceActiveIndex]);

  /* Health Info Agreement handler */
  const acceptHIA = useCallback(async () => {
    if (!consultContext.consult) return;
    await signAgreement(consultContext.consult.patientId, 'Patient', 'GINA', 'core');

    mergeAgreementsState({
      [Agreements.HealthInfo]: AgreementStatus.Agreed,
    });
    advanceActiveIndex();
  }, [advanceActiveIndex, consultContext.consult, mergeAgreementsState]);

  /* Teen Consent handler */
  const acceptTeen = useCallback(async () => {
    if (!consultContext.consult) return;

    createOrUpdatePatient(consultContext.consult.patientId, {
      teenConsent: true,
      planType: planType,
    });
    mergeAgreementsState({
      [Agreements.TeenConsent]: AgreementStatus.Agreed,
    });
    advanceActiveIndex();
  }, [advanceActiveIndex, consultContext.consult, mergeAgreementsState, planType]);

  const acceptNpTeenConsent = useCallback(async () => {
    if (!consultContext.consult) return;

    updateNpTeenConsent(consultContext.consult.patientId, {
      npTeenConsent: true,
      planType: planType,
    });
    mergeAgreementsState({
      [Agreements.NpTeenConsent]: AgreementStatus.Agreed,
    });
    advanceActiveIndex();
  }, [advanceActiveIndex, consultContext.consult, mergeAgreementsState, planType]);

  const formRef = useRef<HTMLFormElement | null>(null);
  const handleClickNext = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // consent to care form submission
    if (formRef.current) {
      formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    } else {
      // no consent to care form is displayed for adults
      // just trigger the submit
      handleSubmit(true, null);
    }
  };

  const handleSubmit = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (isValid: boolean, values: any) => {
      if (!consultContext.consult) return;

      if (agreements[Agreements.ConsentToCare] !== AgreementStatus.NotApplicable) {
        await updateConsentToTreat(consultContext.consult.patientId, !!needsConsent);
      }

      /** At this point, the agreements are in place, so refresh the user context members in the background  */
      if (!isValid) {
        console.log('Handle Submit: Form is not valid, cannot proceed.');
        return;
      }

      if (needsConsent) {
        await addFlagToMember(FlagRefKinds.Custody, consultContext.consult.patientId);
      }

      if (values?.consentGivers) {
        const consentGivers = values.consentGivers as {
          firstName?: string;
          lastName?: string;
          email?: string;
        }[];
        if ('filter' in consentGivers) {
          // @ts-expect-error need to type form values
          const actualGivers: ConsentGiver[] = consentGivers.filter((cg) => cg.firstName && cg.lastName && cg.email);
          if (actualGivers.length) {
            await addConsentGiversToMember(consultContext.consult.patientId, actualGivers);
          }
        }
      }

      await nextStep
        .mutateAsync({
          page: CommonOnboardingPage.Agreements,
          patientId: memberId || consultContext.consult?.patient?.id,
          patientConsultId: consultContext.consult?.id,
        })
        .then((r) => {
          push(r.url);
        });
    },
    [agreements, consultContext.consult, isNpReferralFlow, needsConsent, push],
  );

  const agreementsContent = useMemo(() => {
    const content: Record<Agreements, AccodionContentItem> = {
      [Agreements.TermsAndConditions]: {
        id: 'terms',
        defaultOpen: accordionActiveIndex === 0,
        header: (idx: number) => (
          <div className="agreement-accordion-title">
            <AgreementIndicator status={agreements[Agreements.TermsAndConditions]} idx={idx} />
            <Text variant="body-3" bold>
              Terms & Privacy
            </Text>
          </div>
        ),
        body: () => (
          <div className="agreement-accordion-body">
            <p>
              <Text variant="body-5" style={{ display: 'block' }}>
                By clicking &quot;accept&quot; I consent to, and confirm I have have read and understand the{' '}
                <ExternalLink bold variant="text5" href="https://www.bendhealth.com/terms-consent" className="inline">
                  Terms and Conditions
                </ExternalLink>{' '}
                and the{' '}
                <ExternalLink bold variant="text5" href="https://www.bendhealth.com/app-npp" className="inline">
                  Notice of Privacy Practices
                </ExternalLink>
                .
              </Text>
            </p>
            <Button
              data-testid="terms-and-conditions-accept-button"
              onClick={acceptToC}
              disabled={agreements[Agreements.TermsAndConditions] == AgreementStatus.Agreed}
            >
              {agreements[Agreements.TermsAndConditions] !== AgreementStatus.Agreed ? 'Accept' : 'Accepted'}
            </Button>
            {/* the button above needs something below it to avoid its animation affecting the height of the container */}
            <div className="end" />
          </div>
        ),
      },
      [Agreements.MemberFinancialPolicy]: {
        id: 'member-financial-policy',
        header: (idx: number) => (
          <div className="agreement-accordion-title">
            <AgreementIndicator status={agreements[Agreements.MemberFinancialPolicy]} idx={idx} />
            <Text variant="body-3" bold>
              Member Financial Policy
            </Text>
          </div>
        ),
        body: () => (
          <div className="agreement-accordion-body">
            <p>
              <Text variant="body-5" style={{ display: 'block' }}>
                By clicking &quot;accept&quot; I consent to, and confirm I have have read and understand the{' '}
                <ExternalLink
                  bold
                  variant="text5"
                  href="https://www.bendhealth.com/financial-policy"
                  className="inline"
                >
                  Member Financial Policy
                </ExternalLink>{' '}
                .
              </Text>
            </p>
            <Button
              data-testid="member-financial-policy-accept-button"
              onClick={acceptMemberFinancialPolicy}
              disabled={agreements[Agreements.MemberFinancialPolicy] == AgreementStatus.Agreed}
            >
              {agreements[Agreements.MemberFinancialPolicy] !== AgreementStatus.Agreed ? 'Accept' : 'Accepted'}
            </Button>
            {/* the button above needs something below it to avoid its animation affecting the height of the container */}
            <div className="end" />
          </div>
        ),
      },
      [Agreements.NpInformedConsent]: {
        id: 'np-informed-consent',
        header: (idx: number) => (
          <div className="agreement-accordion-title">
            <AgreementIndicator status={agreements[Agreements.NpInformedConsent]} idx={idx} />
            <Text variant="body-3" bold>
              Informed Consent
            </Text>
          </div>
        ),
        body: () => (
          <div className="agreement-accordion-body">
            <p>
              <Text variant="body-5" style={{ display: 'block' }}>
                By clicking &quot;accept&quot; I consent to, and confirm I have have read and understand the{' '}
                <ExternalLink
                  bold
                  variant="text5"
                  href="https://www.bendhealth.com/informed-consent-neuropsych"
                  className="inline"
                >
                  Neuropsychological Informed Consent
                </ExternalLink>{' '}
                .
              </Text>
            </p>
            <Button
              data-testid="informed-consent-accept-button"
              onClick={acceptNpInformedConsent}
              disabled={agreements[Agreements.NpInformedConsent] == AgreementStatus.Agreed}
            >
              {agreements[Agreements.NpInformedConsent] !== AgreementStatus.Agreed ? 'Accept' : 'Accepted'}
            </Button>

            {/* the button above needs something below it to avoid its animation affecting the height of the container */}
            <div className="end" />
          </div>
        ),
      },
      [Agreements.NpFinancialPolicyPrice]: {
        id: 'np-financial-policy-price',
        header: (idx: number) => (
          <div className="agreement-accordion-title">
            <AgreementIndicator status={agreements[Agreements.NpFinancialPolicyPrice]} idx={idx} />
            <Text variant="body-3" bold>
              Neuropsych Member Financial Policy
            </Text>
          </div>
        ),
        body: () => (
          <div className="agreement-accordion-body">
            <p>
              <Text variant="body-5" style={{ display: 'block' }}>
                By clicking &quot;accept&quot; I consent to, and confirm I have have read and understand the{' '}
                <ExternalLink
                  bold
                  variant="text5"
                  href="https://www.bendhealth.com/financial-policy-neuropsych"
                  className="inline"
                >
                  Neuropsychological Financial Policy
                </ExternalLink>{' '}
                .
              </Text>
            </p>
            <Button
              data-testid="np-financial-policy-accept-button"
              onClick={acceptNpFinancialPolicyPrice}
              disabled={agreements[Agreements.NpFinancialPolicyPrice] == AgreementStatus.Agreed}
            >
              {agreements[Agreements.NpFinancialPolicyPrice] !== AgreementStatus.Agreed ? 'Accept' : 'Accepted'}
            </Button>

            {/* the button above needs something below it to avoid its animation affecting the height of the container */}
            <div className="end" />
          </div>
        ),
      },
      [Agreements.HealthInfo]: {
        id: 'health-info',
        header: (idx: number) => (
          <div className="agreement-accordion-title">
            <AgreementIndicator status={agreements[Agreements.HealthInfo]} idx={idx} />
            <Text variant="body-3" bold>
              Health Information Agreement
            </Text>
          </div>
        ),
        body: () => (
          <div className="agreement-accordion-body">
            <p>
              <Text variant="body-5" style={{ display: 'block' }}>
                By clicking accept, I affirm that I have read and understood the{' '}
                <ExternalLink
                  target="_blank"
                  href="https://www.bendhealth.com/spouse-and-dependent-authorization"
                  rel="noreferrer"
                  variant="text5"
                  bold
                >
                  Spouse and Dependent Authorization
                </ExternalLink>{' '}
                and that I understand participation is completely voluntary; and I agree to participate. I, on behalf of
                myself and my child or teen, authorize the collection and use of my child or teen’s data as described in
                this authorization.
              </Text>
            </p>
            <Button
              data-testid="health-info-accept-button"
              onClick={acceptHIA}
              disabled={agreements[Agreements.HealthInfo] == AgreementStatus.Agreed}
            >
              {agreements[Agreements.HealthInfo] !== AgreementStatus.Agreed ? 'Accept' : 'Accepted'}
            </Button>

            {/* the button above needs something below it to avoid its animation affecting the height of the container */}
            <div className="end" />
          </div>
        ),
      },
      [Agreements.TeenConsent]: {
        id: 'teen-consent',
        header: (idx: number) => (
          <div className="agreement-accordion-title">
            <AgreementIndicator status={agreements[Agreements.TeenConsent]} idx={idx} />
            <Text variant="body-3" bold>
              Teen Consent
            </Text>
          </div>
        ),
        body: () => (
          <div className="agreement-accordion-body">
            <p>
              <Text variant="body-5" block>
                By clicking ‘Accept’, you (teen) have reviewed the{' '}
                <ExternalLink
                  target="_blank"
                  href="https://www.bendhealth.com/teen-consent"
                  rel="noreferrer"
                  variant="text5"
                  bold
                >
                  Teen Consent
                </ExternalLink>{' '}
                which includes details on privacy and how information may be shared as part receiving services from Bend
                Health
              </Text>
            </p>
            <Button
              data-testid="teen-consent-accept-button"
              onClick={acceptTeen}
              disabled={agreements[Agreements.TeenConsent] == AgreementStatus.Agreed}
            >
              {agreements[Agreements.TeenConsent] !== AgreementStatus.Agreed ? 'Accept' : 'Accepted'}
            </Button>

            {/* the button above needs something below it to avoid its animation affecting the height of the container */}
            <div className="end" />
          </div>
        ),
      },
      [Agreements.NpTeenConsent]: {
        id: 'np-teen-consent',
        header: (idx: number) => (
          <div className="agreement-accordion-title">
            <AgreementIndicator status={agreements[Agreements.NpTeenConsent]} idx={idx} />
            <Text variant="body-3" bold>
              Neuropsych Teen Consent
            </Text>
          </div>
        ),
        body: () => (
          <div className="agreement-accordion-body">
            <p>
              <Text variant="body-5" block>
                By clicking ‘Accept’, you (teen) have reviewed the{' '}
                <ExternalLink
                  target="_blank"
                  href="https://www.bendhealth.com/teen-consent-neuropsych"
                  rel="noreferrer"
                  variant="text5"
                  bold
                >
                  Teen Consent
                </ExternalLink>{' '}
                which includes details on privacy and how information may be shared as part receiving services from Bend
                Health
              </Text>
            </p>
            <Button
              data-testid="np-teen-consent-accept-button"
              onClick={acceptNpTeenConsent}
              disabled={agreements[Agreements.NpTeenConsent] == AgreementStatus.Agreed}
            >
              {agreements[Agreements.NpTeenConsent] !== AgreementStatus.Agreed ? 'Accept' : 'Accepted'}
            </Button>

            {/* the button above needs something below it to avoid its animation affecting the height of the container */}
            <div className="end" />
          </div>
        ),
      },
      [Agreements.ConsentToCare]: {
        id: 'consent-to-care',
        header: (idx: number) => (
          <div className="agreement-accordion-title">
            <AgreementIndicator status={agreements[Agreements.ConsentToCare]} idx={idx} />
            <Text variant="body-3" bold>
              Consent to Treat a Minor
            </Text>
          </div>
        ),
        body: () => (
          <FormProvider controlRef={formRef} onSubmit={handleSubmit}>
            <ConsentAgreementBody agreed={needsConsent} setAgreed={setNeedsConsent} />
          </FormProvider>
        ),
      },
    };

    const filteredContent: AccodionContentItem[] = [
      content[Agreements.TermsAndConditions],
      isAgreementStatusRequired(agreements[Agreements.MemberFinancialPolicy])
        ? content[Agreements.MemberFinancialPolicy]
        : undefined,
      isAgreementStatusRequired(agreements[Agreements.NpInformedConsent])
        ? content[Agreements.NpInformedConsent]
        : undefined,
      isAgreementStatusRequired(agreements[Agreements.NpFinancialPolicyPrice])
        ? content[Agreements.NpFinancialPolicyPrice]
        : undefined,
      isAgreementStatusRequired(agreements[Agreements.HealthInfo]) ? content[Agreements.HealthInfo] : undefined,
      isAgreementStatusRequired(agreements[Agreements.TeenConsent]) ? content[Agreements.TeenConsent] : undefined,
      isAgreementStatusRequired(agreements[Agreements.NpTeenConsent]) ? content[Agreements.NpTeenConsent] : undefined,
      isAgreementStatusRequired(agreements[Agreements.ConsentToCare]) ? content[Agreements.ConsentToCare] : undefined,
    ].filter<AccodionContentItem>((x): x is AccodionContentItem => !!x);

    filteredContent.forEach((item, idx) => {
      // dont auto-close the consent givers section (it "completes" but has optional fields)
      if (idx === filteredContent.length - 1 && accordionActiveIndex !== idx) {
        return;
      }

      item.defaultOpen = accordionActiveIndex === idx;
    });

    return filteredContent;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accordionActiveIndex,
    agreements,
    acceptToC,
    acceptHIA,
    acceptTeen,
    handleSubmit,
    // needs consent is ignored from useMemo because we dont want to force a rerender
    needsConsent,
    setNeedsConsent,
  ]);

  const canGoNext = useMemo(() => {
    return Object.values(agreements).every((status) => isAgreementStatusResolved(status));
  }, [agreements]);

  if (isPendingAgreementsLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="AgreementsPage grid-12 m-grid-12">
      {!isNpReferralFlow ? (
        <>
          <div className="span-6">
            <AgreementsIntroPanel isReferral={state.isReferral} referringName={state.referralName} />
          </div>
          <div className="span-6">
            <p className="electronic-signature-ack">
              <Text variant="body-6">
                I acknowledge that by electronically signing below, I am providing my legal signature, confirming that I
                have read, understood, and agree to the terms and conditions outlined. I consent to treatment as
                described. I also confirm that I am the member or have the legal authority to sign on behalf of the
                member. I understand that my electronic signature has the same legal effect as a handwritten signature.
              </Text>
            </p>
            {<Accordion content={agreementsContent} />}
          </div>
        </>
      ) : (
        <>
          <div className="span-12 mx-auto" style={{ maxWidth: '800px' }}>
            <div className="pt-12 sm:pt-4 px-2 -mt-10">
              <BackArrowButton onClick={popNavigation} />
            </div>
            <div className="mb-6">
              <Text variant="h3">Agreements</Text>
              <Text variant="body-5" className="mt-2">
                Please review and accept the following consents and agreements:
              </Text>
            </div>
            <div>
              <p className="electronic-signature-ack">
                <Text variant="body-6" style={{ textAlign: 'left' }}>
                  I acknowledge that by electronically signing below, I am providing my legal signature, confirming that
                  I have read, understood, and agree to the terms and conditions outlined. I consent to treatment as
                  described. I also confirm that I am the member or have the legal authority to sign on behalf of the
                  member. I understand that my electronic signature has the same legal effect as a handwritten
                  signature.
                </Text>
              </p>
              {<Accordion content={agreementsContent} />}
            </div>
          </div>
        </>
      )}

      <FooterPortal>
        <FooterButton isLoading={false} disabled={!canGoNext || nextStep.isLoading} onClick={handleClickNext}>
          Next
        </FooterButton>
      </FooterPortal>
    </div>
  );
};

function getActiveIndex(state: Record<Agreements, AgreementStatus>) {
  const order = buildOrder(state);

  // Find the first waiting agreement in our ordered list
  for (let i = 0; i < order.length; i++) {
    const agreement = order[i];
    if (state[agreement] === AgreementStatus.Waiting) {
      return i;
    }
  }

  // If no agreements are waiting, return the last index
  return order.length - 1;
}

function buildOrder(state: Record<Agreements, AgreementStatus>) {
  const order = [Agreements.TermsAndConditions];

  if (isAgreementStatusRequired(state[Agreements.MemberFinancialPolicy])) {
    order.push(Agreements.MemberFinancialPolicy);
  }

  // Add NP specific agreements if in NP flow
  if (isAgreementStatusRequired(state[Agreements.NpInformedConsent])) {
    order.push(Agreements.NpInformedConsent);
  }
  if (isAgreementStatusRequired(state[Agreements.NpFinancialPolicyPrice])) {
    order.push(Agreements.NpFinancialPolicyPrice);
  }
  if (isAgreementStatusRequired(state[Agreements.NpTeenConsent])) {
    order.push(Agreements.NpTeenConsent);
  }

  // Add non-NP specific agreements
  if (isAgreementStatusRequired(state[Agreements.HealthInfo])) {
    order.push(Agreements.HealthInfo);
  }
  if (isAgreementStatusRequired(state[Agreements.TeenConsent])) {
    order.push(Agreements.TeenConsent);
  }

  // Always add ConsentToCare last
  order.push(Agreements.ConsentToCare);

  return order;
}

export default function AgreementsPage() {
  useEnsureAuthenticated();

  const { isMobileView } = useBrowserContext();
  const user = useCurrentUser();
  const { consultId } = useParams();

  return (
    <ConsultProvider consultId={consultId as string}>
      <GlobalHeader user={user.state.account} isMobileView={isMobileView} />
      <div className="AgreementsPage-layout-wrapper">
        <AgreementsPageContent />
      </div>
    </ConsultProvider>
  );
}
